/**
 * API client — adapter from the original PP/arx server contract to
 * the new CF Worker API.
 *
 * Strategy:
 *   - Keep the exported function signatures identical so the rest of
 *     the frontend (PP-copied components) needs no changes.
 *   - Map each call to the equivalent CF endpoint where one exists.
 *   - Where the backend isn't there yet (folders, phase, mode,
 *     versions, members, profiles, share, context, confluence,
 *     admin), throw `NotImplementedError` with a useful message OR
 *     return a sensible default (e.g. empty list, synthetic single
 *     owner) so the UI keeps rendering.
 *   - Never return the PP {ok, data} envelope — CF returns raw JSON;
 *     errors come back as `{error: {code, message}}` and we throw.
 */

import type { Deck, Message, Profile, Folder } from '../types';
import { apiFetch, apiUpload } from './auth-fetch';

const API = '/api';

export class NotImplementedError extends Error {
  constructor(feature: string) {
    super(`${feature} is not implemented in this build yet.`);
    this.name = 'NotImplementedError';
  }
}

interface CfErrorBody {
  error?: { code: string; message: string };
}

async function cfRequest<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const init: RequestInit = {
    method,
    headers: body ? { 'Content-Type': 'application/json' } : {},
  };
  if (body !== undefined) init.body = JSON.stringify(body);
  const res = await apiFetch(`${API}${path}`, {
    ...init,
    suppressAuthExpired: path === '/auth/login',
  });
  if (!res.ok) {
    let message = res.statusText || `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as CfErrorBody;
      if (body.error?.message) message = body.error.message;
    } catch {
      /* not json */
    }
    throw new Error(message);
  }
  if (res.status === 204) return undefined as unknown as T;
  return (await res.json()) as T;
}

// ── Auth ─────────────────────────────────────────────────────
//
// PP's login took (username, password) and returned
// {token, user_id, username, role}. The CF API uses email + password;
// the returned token shape is synthesized because auth is cookie-based.

export async function login(emailOrUsername: string, password: string) {
  const res = await cfRequest<{
    user: {
      id: string;
      email: string;
      display_name: string | null;
      avatar_url: string | null;
    };
  }>('POST', '/auth/login', { email: emailOrUsername, password });
  return {
    token: 'cookie-auth',
    user_id: res.user.id,
    username: res.user.display_name || res.user.email,
    role: 'user',
  };
}

export async function register(
  username: string,
  email: string,
  password: string,
) {
  const res = await cfRequest<{
    user: {
      id: string;
      email: string;
      display_name: string | null;
      avatar_url: string | null;
    };
  }>('POST', '/auth/register', {
    email: email || username,
    password,
    display_name: username,
  });
  return {
    token: 'cookie-auth',
    user_id: res.user.id,
    username: res.user.display_name || res.user.email,
    role: 'user',
  };
}

// ── Workspaces ───────────────────────────────────────────────

export interface Workspace {
  id: string;
  name: string;
  type: 'personal' | 'team';
  owner_id: string;
  my_role: string;
  avatar_url?: string;
  created_at: number;
  updated_at: number;
}

export type WorkspaceMemberRole = 'owner' | 'admin' | 'editor' | 'viewer';

export interface WorkspaceMember {
  id: string;
  workspace_id: string;
  user_id: string;
  role: WorkspaceMemberRole;
  invited_by: string | null;
  joined_at: number;
  updated_at: number | null;
  email?: string;
  display_name?: string | null;
  avatar_url?: string | null;
}

interface CfWorkspaceMember {
  workspace_id: string;
  user_id: string;
  role: WorkspaceMemberRole;
  invited_by: string | null;
  created_at: number;
  updated_at: number | null;
  user: {
    email: string;
    display_name: string | null;
    avatar_url: string | null;
  } | null;
}

interface CfWorkspace {
  id: string;
  name: string;
  owner_id: string;
  created_at: number;
}

function adaptWorkspace(w: CfWorkspace, currentUserId?: string): Workspace {
  return {
    id: w.id,
    name: w.name,
    owner_id: w.owner_id,
    type: 'team',
    my_role: currentUserId === w.owner_id ? 'owner' : 'viewer',
    created_at: w.created_at,
    updated_at: w.created_at,
  };
}

function adaptWorkspaceMember(m: CfWorkspaceMember): WorkspaceMember {
  return {
    id: `${m.workspace_id}-${m.user_id}`,
    workspace_id: m.workspace_id,
    user_id: m.user_id,
    role: m.role,
    invited_by: m.invited_by,
    joined_at: m.created_at,
    updated_at: m.updated_at,
    email: m.user?.email,
    display_name: m.user?.display_name,
    avatar_url: m.user?.avatar_url,
  };
}

export async function listWorkspaces(): Promise<Workspace[]> {
  const me = await cfRequest<{ user: { id: string }; workspaces: CfWorkspace[] }>(
    'GET',
    '/auth/me',
  );
  return me.workspaces.map((w) => adaptWorkspace(w, me.user.id));
}

export async function listWorkspaceDecks(wsId: string): Promise<Deck[]> {
  const res = await cfRequest<{ decks: CfDeck[] }>(
    'GET',
    `/workspaces/${wsId}/decks`,
  );
  return res.decks.map(adaptDeck);
}

export async function createWorkspace(name: string): Promise<Workspace> {
  const res = await cfRequest<{ workspace: CfWorkspace }>('POST', '/workspaces', {
    name,
  });
  return {
    ...adaptWorkspace(res.workspace),
    my_role: 'owner',
  };
}

export async function listWorkspaceMembers(
  workspaceId: string,
): Promise<{ members: WorkspaceMember[]; me: { role: WorkspaceMemberRole } }> {
  const res = await cfRequest<{
    members: CfWorkspaceMember[];
    me: { role: WorkspaceMemberRole };
  }>('GET', `/workspaces/${workspaceId}/members`);
  return {
    members: res.members.map(adaptWorkspaceMember),
    me: res.me,
  };
}

export async function inviteWorkspaceMember(
  workspaceId: string,
  input: { email?: string; user_id?: string; role?: Exclude<WorkspaceMemberRole, 'owner'> },
): Promise<WorkspaceMember> {
  const res = await cfRequest<{ member: CfWorkspaceMember }>(
    'POST',
    `/workspaces/${workspaceId}/members`,
    input,
  );
  return adaptWorkspaceMember(res.member);
}

export async function setWorkspaceMemberRole(
  workspaceId: string,
  userId: string,
  role: Exclude<WorkspaceMemberRole, 'owner'>,
): Promise<WorkspaceMember> {
  const res = await cfRequest<{ member: CfWorkspaceMember }>(
    'PATCH',
    `/workspaces/${workspaceId}/members/${encodeURIComponent(userId)}`,
    { role },
  );
  return adaptWorkspaceMember(res.member);
}

export async function removeWorkspaceMember(
  workspaceId: string,
  userId: string,
): Promise<void> {
  await cfRequest(
    'DELETE',
    `/workspaces/${workspaceId}/members/${encodeURIComponent(userId)}`,
  );
}

export async function transferWorkspaceOwner(
  workspaceId: string,
  newOwnerId: string,
): Promise<WorkspaceMember[]> {
  const res = await cfRequest<{ members: CfWorkspaceMember[] }>(
    'POST',
    `/workspaces/${workspaceId}/members/${encodeURIComponent(newOwnerId)}/transfer-owner`,
  );
  return res.members.map(adaptWorkspaceMember);
}

// ── Decks ────────────────────────────────────────────────────

interface CfDeckSignals {
  is_actively_working: boolean;
  turn_status?: 'idle' | 'running';
  turn_id?: string | null;
  started_at?: number | null;
  task_started_at?: number | null;
  task_version?: string | null;
  total_active_ms?: number;
  versions_active_ms?: Record<string, number>;
  /** Generic artifact viewer (PP main #87 alignment). */
  artifacts?: CfArtifactSignal[];
  active_artifact_id?: string | null;
}

interface CfArtifactSignal {
  id: string;
  display_name: string;
  mime: string;
  kind: 'deck-group' | 'file';
  source_format?: 'html' | 'pptx-template' | 'pptx-svg';
  version_label: string;
  size?: number;
  updated_at: number;
  download_url: string;
  preview_url: string;
}

interface CfDeck {
  id: string;
  workspace_id: string;
  owner_id: string;
  title: string;
  phase?: string;
  mode?: string | null;
  theme?: string | null;
  folder_id?: string | null;
  current_version?: string | null;
  active_task?: string | null;
  task_turn_id?: string | null;
  task_started_at?: number | null;
  task_last_progress_at?: number | null;
  task_error?: string | null;
  created_at: number;
  updated_at: number;
  signals?: CfDeckSignals;
  me?: {
    workspace_role: 'owner' | 'admin' | 'editor' | 'viewer' | null;
    deck_role: 'owner' | 'editor' | 'viewer' | null;
    capabilities: {
      read: boolean;
      chat: boolean;
      write: boolean;
      manage: boolean;
    };
  };
}

function adaptDeck(d: CfDeck): Deck {
  return {
    id: d.id,
    arx_conversation_id: d.id,
    user_id: d.owner_id,
    owner_id: d.owner_id,
    title: d.title,
    phase: (d.phase as Deck['phase']) ?? 'gather',
    mode: (d.mode as Deck['mode']) ?? null,
    theme: (d.theme as Deck['theme']) ?? null,
    disabled_context_keys: null,
    folder_id: d.folder_id ?? null,
    current_version: d.current_version ?? null,
    active_task: d.active_task ?? null,
    task_turn_id: d.task_turn_id ?? null,
    task_started_at: d.task_started_at ?? null,
    task_last_progress_at: d.task_last_progress_at ?? null,
    task_error: d.task_error ?? null,
    created_at: d.created_at,
    updated_at: d.updated_at,
    access: d.me,
    ...(d.signals && typeof d.signals.is_actively_working === 'boolean'
      ? {
          signals: {
            is_actively_working: d.signals.is_actively_working,
            turn_status: d.signals.turn_status,
            turn_id: d.signals.turn_id ?? null,
            started_at: d.signals.started_at ?? null,
            task_started_at: d.signals.task_started_at ?? null,
            task_version: d.signals.task_version ?? null,
            total_active_ms: d.signals.total_active_ms ?? 0,
            versions_active_ms: d.signals.versions_active_ms ?? {},
            artifacts: d.signals.artifacts ?? [],
            active_artifact_id: d.signals.active_artifact_id ?? null,
          },
        }
      : {}),
  };
}

export const createDeck = async (
  title?: string,
  _narrative?: string,
  _style?: string,
  _templateRef?: string,
  folderId?: string,
  workspaceId?: string,
): Promise<Deck> => {
  const wsId = workspaceId ?? (await listWorkspaces())[0]?.id;
  if (!wsId) throw new Error('No workspace available.');
  const res = await cfRequest<{ deck: CfDeck }>(
    'POST',
    `/workspaces/${wsId}/decks`,
    {
      title: title || 'Untitled deck',
      ...(folderId ? { folder_id: folderId } : {}),
    },
  );
  return adaptDeck(res.deck);
};

export async function listDecks(workspaceId?: string): Promise<Deck[]> {
  if (workspaceId) return listWorkspaceDecks(workspaceId);
  const all: Deck[] = [];
  for (const ws of await listWorkspaces()) {
    all.push(...(await listWorkspaceDecks(ws.id)));
  }
  return all;
}

export async function getDeck(id: string): Promise<Deck> {
  const res = await cfRequest<{ deck: CfDeck }>('GET', `/decks/${id}`);
  return adaptDeck(res.deck);
}

export async function updateDeck(
  id: string,
  updates: Partial<Deck>,
): Promise<Deck> {
  const patch: Record<string, unknown> = {};
  if (updates.title !== undefined) patch.title = updates.title;
  if (updates.folder_id !== undefined) patch.folder_id = updates.folder_id;
  const res = await cfRequest<{ deck: CfDeck }>('PATCH', `/decks/${id}`, patch);
  return adaptDeck(res.deck);
}

export async function deleteDeck(id: string): Promise<void> {
  await cfRequest<{ ok: true }>('DELETE', `/decks/${id}`);
}

export async function clearMessages(_deckId: string): Promise<void> {
  throw new NotImplementedError('Clear messages');
}

export async function advancePhase(id: string, phase: string): Promise<Deck> {
  const res = await cfRequest<{ deck: CfDeck }>('POST', `/decks/${id}/phase`, {
    phase,
  });
  return adaptDeck(res.deck);
}

// ── Build mode ───────────────────────────────────────────────

// DeckMode + DeckTheme + setDeckMode + PATCH /decks/:id/mode removed
// in Stage 4 of #87 alignment. Mode is per-turn agent decision now
// (signals.artifacts.source_format), not a deck-level column.

// ── Versions ─────────────────────────────────────────────────

export interface DeckVersion {
  key: string;
  title: string;
  slides: number;
  created_at: number;
  updated_at: number;
}

export async function listVersions(
  deckId: string,
): Promise<{ versions: DeckVersion[]; current: string }> {
  const res = await cfRequest<{
    ok: boolean;
    data?: { versions: DeckVersion[]; current: string };
  }>('GET', `/decks/${deckId}/versions`);
  return {
    versions: res.data?.versions ?? [],
    current: res.data?.current ?? '',
  };
}

export async function createVersion(
  _deckId: string,
  _name: string,
): Promise<{ version: string }> {
  // CF rebuild materialises versions automatically when the agent
  // emits a slide-formatted reply — there's no explicit create call.
  throw new NotImplementedError('Manual version creation');
}

export async function switchVersion(
  deckId: string,
  name: string,
): Promise<{ current: string }> {
  const res = await cfRequest<{
    ok: boolean;
    data?: { current: string };
  }>('PUT', `/decks/${deckId}/versions/current`, { name });
  return { current: res.data?.current ?? name };
}

export interface SlideRow {
  presentation_name: string;
  slide_num: number;
  title: string;
  body: string;
  updated_at: number;
}

export async function getVersion(
  deckId: string,
  name: string,
): Promise<{
  presentation: { name: string; title: string | null; slides_count: number };
  slides: SlideRow[];
}> {
  const res = await cfRequest<{
    ok: boolean;
    data?: {
      presentation: { name: string; title: string | null; slides_count: number };
      slides: SlideRow[];
    };
  }>('GET', `/decks/${deckId}/versions/${encodeURIComponent(name)}`);
  if (!res.data) throw new Error('Empty version response');
  return res.data;
}

export async function deleteVersion(
  deckId: string,
  name: string,
): Promise<void> {
  await cfRequest('DELETE', `/decks/${deckId}/versions/${encodeURIComponent(name)}`);
}

export async function saveSlide(
  deckId: string,
  versionName: string,
  patch: { slide_num: number; title?: string; body?: string },
): Promise<SlideRow> {
  const res = await cfRequest<{
    ok: boolean;
    data?: { slide: SlideRow };
  }>(
    'PUT',
    `/decks/${deckId}/versions/${encodeURIComponent(versionName)}/slide`,
    patch,
  );
  if (!res.data) throw new Error('Empty slide response');
  return res.data.slide;
}

export function exportPptxUrl(deckId: string, version?: string | null): string {
  const v = version ? `?version=${encodeURIComponent(version)}` : '';
  return `${API}/decks/${deckId}/export.pptx${v}`;
}

// ── Chat attachments (multimodal input) ──────────────────────

export interface ChatAttachment {
  r2_key: string;
  mime: string;
  size: number;
  name: string;
}

/** Allowed input MIMEs. Matches the server allow list. */
export const CHAT_ATTACHMENT_ACCEPT =
  'image/png,image/jpeg,image/jpg,image/gif,image/webp';

export const CHAT_ATTACHMENT_MAX_BYTES = 3_500_000;
export const CHAT_ATTACHMENT_MAX_PER_TURN = 5;

export async function uploadChatAttachment(
  deckId: string,
  file: File,
): Promise<ChatAttachment> {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${API}/decks/${deckId}/chat-attachments`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    const msg = body?.error?.message || res.statusText || 'Upload failed';
    throw new Error(msg);
  }
  return (await res.json()) as ChatAttachment;
}

export function chatAttachmentDownloadUrl(
  deckId: string,
  r2Key: string,
): string {
  return `${API}/decks/${deckId}/chat-attachments?key=${encodeURIComponent(r2Key)}`;
}

// ── Mosoo Thread API ─────────────────────────────────────────

export interface DeckMosooBinding {
  created_at: number;
  deck_id: string;
  mosoo_agent_id: string;
  mosoo_thread_id: string | null;
  updated_at: number;
}

export type MosooThreadEventInput =
  | {
      clientRequestId?: string | null;
      resources?: { file_id: string; type: 'file' }[];
      text: string;
      type: 'user_message';
    }
  | {
      decision: 'allow_once' | 'reject_once';
      requestId: string;
      type: 'permission_decision';
    }
  | {
      runId?: string | null;
      type: 'user_interrupt';
    };

export interface MosooThreadEvent {
  content: string;
  durationMs: number | null;
  id: string;
  occurredAt: string;
  runId: string | null;
  status: string;
  tokens: number | null;
  type: string;
}

export interface MosooThreadFile {
  committed: boolean;
  createdAt: string;
  id: string;
  kind: 'artifact' | 'attachment';
  mimeType: string | null;
  name: string;
  size: number;
}

export interface MosooPublicFile {
  createdAt: string;
  id: string;
  mimeType: string | null;
  name: string;
  size: number;
}

export async function getDeckMosooBinding(deckId: string) {
  return cfRequest<{
    binding: DeckMosooBinding | null;
    mosoo: {
      client_configured: boolean;
      client_status: { configured: boolean; missing: string[] };
      default_agent_id_configured: boolean;
      runtime_configured: boolean;
      status: { configured: boolean; missing: string[] };
    };
    ok: true;
  }>('GET', `/decks/${deckId}/mosoo-binding`);
}

export async function saveDeckMosooBinding(
  deckId: string,
  input: {
    create_thread?: boolean;
    mosoo_agent_id?: string | null;
    mosoo_thread_id?: string | null;
  },
) {
  return cfRequest<{
    binding: DeckMosooBinding;
    created_thread: unknown;
    ok: true;
  }>('PUT', `/decks/${deckId}/mosoo-binding`, input);
}

export async function ensureDeckMosooThread(
  deckId: string,
): Promise<DeckMosooBinding | null> {
  const current = await getDeckMosooBinding(deckId);
  if (!current.mosoo.client_configured) return null;
  if (!current.binding && !current.mosoo.default_agent_id_configured) return null;
  if (current.binding?.mosoo_thread_id) return current.binding;

  const created = await saveDeckMosooBinding(deckId, { create_thread: true });
  return created.binding;
}

export async function deleteDeckMosooBinding(deckId: string) {
  return cfRequest<{ binding: null; ok: true }>(
    'DELETE',
    `/decks/${deckId}/mosoo-binding`,
  );
}

export async function getDeckMosooThread(deckId: string) {
  return cfRequest<{ ok: true; result: unknown }>(
    'GET',
    `/decks/${deckId}/mosoo-thread`,
  );
}

export async function sendDeckMosooThreadEvents(
  deckId: string,
  input:
    | {
        event_limit?: number;
        events: MosooThreadEventInput[];
        timeout_ms?: number;
        wait?: boolean;
      }
    | {
        event_limit?: number;
        resources?: { file_id: string; type: 'file' }[];
        text: string;
        timeout_ms?: number;
        wait?: boolean;
      },
) {
  return cfRequest<{
    created: unknown;
    ok: true;
    run_id: string | null;
    sent: unknown;
    thread_id: string;
    waited: unknown;
  }>('POST', `/decks/${deckId}/mosoo-thread/events`, input);
}

export async function listDeckMosooThreadEvents(
  deckId: string,
  limit = 100,
) {
  return cfRequest<{
    ok: true;
    result: { events: MosooThreadEvent[]; truncated: boolean };
  }>('GET', `/decks/${deckId}/mosoo-thread/events?limit=${encodeURIComponent(String(limit))}`);
}

export async function listDeckMosooThreadFiles(deckId: string) {
  return cfRequest<{ ok: true; result: { files: MosooThreadFile[] } }>(
    'GET',
    `/decks/${deckId}/mosoo-thread/files`,
  );
}

export async function uploadDeckMosooThreadFile(
  deckId: string,
  file: File,
  options: { onProgress?: (loaded: number, total: number) => void; signal?: AbortSignal } = {},
) {
  const form = new FormData();
  form.append('file', file);
  const res = await apiUpload(`${API}/decks/${deckId}/mosoo-thread/files`, form, options);
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    const msg = body?.error?.message || res.statusText || 'Upload failed';
    throw new Error(msg);
  }
  return (await res.json()) as { ok: true; result: { file: MosooPublicFile } };
}

export function deckMosooThreadEventStreamUrl(deckId: string, limit = 100): string {
  return `${API}/decks/${deckId}/mosoo-thread/events/stream?limit=${encodeURIComponent(String(limit))}`;
}

export function deckMosooThreadFileContentUrl(deckId: string, fileId: string): string {
  return `${API}/decks/${deckId}/mosoo-thread/files/${encodeURIComponent(fileId)}/content`;
}

// ── Tulpas ───────────────────────────────────────────────────

export interface PpTulpa {
  id: string;
  slug: string;
  name: string;
  workspace_id: string | null;
  system_prompt: string;
  description: string | null;
  is_default: boolean;
  is_system: boolean;
  talents: { id: string; slug: string; sort_order: number }[];
  created_at: number;
  updated_at: number;
}

export async function listPpTulpas(workspaceId: string): Promise<PpTulpa[]> {
  const res = await cfRequest<{ tulpas: PpTulpa[] }>(
    'GET',
    `/tulpas?ws=${encodeURIComponent(workspaceId)}`,
  );
  return res.tulpas;
}

export async function createPpTulpa(
  workspaceId: string,
  input: {
    slug: string;
    name: string;
    system_prompt: string;
    description?: string | null;
    talent_slugs?: string[];
  },
): Promise<PpTulpa> {
  const res = await cfRequest<{ tulpa: PpTulpa }>(
    'POST',
    `/tulpas?ws=${encodeURIComponent(workspaceId)}`,
    input,
  );
  return res.tulpa;
}

export async function updatePpTulpa(
  workspaceId: string,
  slug: string,
  patch: {
    name?: string;
    system_prompt?: string;
    description?: string | null;
    is_default?: boolean;
    talent_slugs?: string[];
  },
): Promise<PpTulpa> {
  const res = await cfRequest<{ tulpa: PpTulpa }>(
    'PATCH',
    `/tulpas/${encodeURIComponent(slug)}?ws=${encodeURIComponent(workspaceId)}`,
    patch,
  );
  return res.tulpa;
}

export async function deletePpTulpa(
  workspaceId: string,
  slug: string,
): Promise<void> {
  await cfRequest(
    'DELETE',
    `/tulpas/${encodeURIComponent(slug)}?ws=${encodeURIComponent(workspaceId)}`,
  );
}

export async function updateDefaultWriterTalentBindings(
  talentSlugs: string[],
): Promise<{ ok: boolean; talent_slugs: string[] }> {
  return cfRequest('PUT', '/admin/system-tulpas/pp-writer/talents', {
    talent_slugs: talentSlugs,
  });
}

// ── Talents ──────────────────────────────────────────────────

export interface PpTalent {
  id: string;
  slug: string;
  name: string;
  workspace_id: string | null;
  is_system: boolean;
  kind: 'prompt' | 'script';
  runtime: string | null;
  entry: string | null;
  description: string | null;
  files: { path: string; size: number }[];
  created_at: number;
  updated_at: number;
}

export async function listPpTalents(workspaceId: string): Promise<PpTalent[]> {
  const res = await cfRequest<{ talents: PpTalent[] }>(
    'GET',
    `/talents?ws=${encodeURIComponent(workspaceId)}`,
  );
  return res.talents;
}

export async function createPromptTalent(
  workspaceId: string,
  input: {
    slug: string;
    name: string;
    body: string;
    description?: string | null;
  },
): Promise<PpTalent> {
  const res = await cfRequest<{ talent: PpTalent }>(
    'POST',
    `/talents?ws=${encodeURIComponent(workspaceId)}`,
    input,
  );
  return res.talent;
}

// Multipart upload for script-style talents.
export async function uploadScriptTalent(
  workspaceId: string,
  meta: {
    slug: string;
    name: string;
    runtime?: string;
    entry?: string;
    description?: string | null;
  },
  files: { path: string; file: File }[],
): Promise<PpTalent> {
  const fd = new FormData();
  fd.set('slug', meta.slug);
  fd.set('name', meta.name);
  fd.set('kind', 'script');
  if (meta.runtime) fd.set('runtime', meta.runtime);
  if (meta.entry) fd.set('entry', meta.entry);
  if (meta.description != null) fd.set('description', meta.description);
  for (const f of files) {
    fd.append(f.path, f.file, f.path);
  }
  const res = await apiFetch(
    `${API}/talents?ws=${encodeURIComponent(workspaceId)}`,
    {
      method: 'POST',
      body: fd,
    },
  );
  if (!res.ok) {
    let message = res.statusText || `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as CfErrorBody;
      if (body.error?.message) message = body.error.message;
    } catch {
      /* not json */
    }
    throw new Error(message);
  }
  const body = (await res.json()) as { talent: PpTalent };
  return body.talent;
}

export async function deletePpTalent(
  workspaceId: string,
  slug: string,
): Promise<void> {
  await cfRequest(
    'DELETE',
    `/talents/${encodeURIComponent(slug)}?ws=${encodeURIComponent(workspaceId)}`,
  );
}

// ── Deck ↔ tulpa binding + manual talent run ─────────────────

export async function bindDeckTulpa(
  deckId: string,
  tulpaSlug: string | null,
): Promise<void> {
  await cfRequest('PUT', `/decks/${deckId}/tulpa`, {
    tulpa_slug: tulpaSlug,
  });
}

export interface TalentRunOutputClient {
  path: string;
  size: number;
  r2_key: string;
  content_type?: string;
}

export interface TalentRunResultClient {
  run_id: string;
  talent_slug: string;
  exit_code: number;
  stdout_excerpt: string;
  stderr_excerpt: string;
  duration_ms: number;
  outputs: TalentRunOutputClient[];
}

export async function runTalent(
  deckId: string,
  input: {
    talent_slug: string;
    args?: Record<string, string>;
    input_keys?: string[];
  },
): Promise<TalentRunResultClient> {
  const res = await cfRequest<{
    ok: boolean;
    data: TalentRunResultClient;
    error?: string;
  }>('POST', `/decks/${deckId}/talents/run`, input);
  if (!res.ok) throw new Error(res.error ?? 'Talent run failed');
  return res.data;
}

// ── Members ──────────────────────────────────────────────────

export interface DeckMember {
  id: string;
  deck_id: string;
  member_id: string;
  member_type: 'human' | 'agent';
  role: string;
  tulpa_id?: string;
  invited_by?: string | null;
  joined_at: number;
  status: string;
  // Hydrated by the server (PR #16 onwards).
  email?: string;
  display_name?: string | null;
  avatar_url?: string | null;
}

// Wire shape from the CF backend (PR #16).
interface CfDeckMember {
  deck_id: string;
  user_id: string;
  role: 'owner' | 'editor' | 'viewer';
  invited_by: string | null;
  created_at: number;
  updated_at: number;
  user: {
    email: string;
    display_name: string | null;
    avatar_url: string | null;
  } | null;
}

function adaptDeckMember(deckId: string, m: CfDeckMember): DeckMember {
  return {
    id: `${deckId}-${m.user_id}`,
    deck_id: m.deck_id,
    member_id: m.user_id,
    member_type: 'human',
    role: m.role,
    invited_by: m.invited_by,
    joined_at: m.created_at,
    status: 'active',
    email: m.user?.email,
    display_name: m.user?.display_name,
    avatar_url: m.user?.avatar_url,
  };
}

export interface DeckAgent {
  tulpa_id: string;
  slug: string;
  name: string;
  description: string | null;
  sort_order: number;
}

export type DeckChatMode = 'relay';

export async function listDeckAgents(
  deckId: string,
): Promise<{ agents: DeckAgent[]; chat_mode: DeckChatMode }> {
  const res = await cfRequest<{
    ok: boolean;
    agents: DeckAgent[];
    chat_mode?: DeckChatMode;
  }>('GET', `/decks/${deckId}/agents`);
  return { agents: res.agents ?? [], chat_mode: res.chat_mode ?? 'relay' };
}

export async function setDeckAgents(
  deckId: string,
  tulpa_slugs: string[],
): Promise<DeckAgent[]> {
  const res = await cfRequest<{ ok: boolean; agents: DeckAgent[] }>(
    'PUT',
    `/decks/${deckId}/agents`,
    { tulpa_slugs },
  );
  return res.agents ?? [];
}

export async function addDeckAgent(
  deckId: string,
  slug: string,
): Promise<DeckAgent[]> {
  const res = await cfRequest<{ ok: boolean; agents: DeckAgent[] }>(
    'POST',
    `/decks/${deckId}/agents/${encodeURIComponent(slug)}`,
  );
  return res.agents ?? [];
}

export async function removeDeckAgent(
  deckId: string,
  slug: string,
): Promise<DeckAgent[]> {
  const res = await cfRequest<{ ok: boolean; agents: DeckAgent[] }>(
    'DELETE',
    `/decks/${deckId}/agents/${encodeURIComponent(slug)}`,
  );
  return res.agents ?? [];
}

export async function setDeckChatMode(
  deckId: string,
  mode: DeckChatMode,
): Promise<void> {
  await cfRequest('PUT', `/decks/${deckId}/chat-mode`, { mode });
}

export async function listDeckMembers(deckId: string): Promise<DeckMember[]> {
  // Workspace-level human members + per-deck agent roster. Both
  // surface as DeckMember rows so the popover renders them in one
  // pane. Agents come from the deck_agents table (Slice 8 schema);
  // previously the client synthesized a single agent row from
  // decks.tulpa_id, which couldn't represent a multi-tulpa roster.
  //
  // Used to also fetch /decks/:id here just for deck.created_at →
  // assigned to every agent row's joined_at. That third GET added a
  // ~1.6s parallel call on every deck-open hot path. Replaced with
  // the owner's deck_members.created_at (decks + deck_members are
  // written in the same D1 batch in createDeck, so the two values
  // are equal on freshly-created decks and "close enough" on legacy
  // ones — joined_at on agent rows is decorative; nothing branches
  // on its exact value).
  const [humansRes, agentsRes] = await Promise.all([
    cfRequest<{ members: CfDeckMember[] }>('GET', `/decks/${deckId}/members`),
    listDeckAgents(deckId),
  ]);
  const humans = humansRes.members.map((m) => adaptDeckMember(deckId, m));
  const owner = humansRes.members.find((m) => m.role === 'owner');
  const agentJoinedAt = owner?.created_at ?? humansRes.members[0]?.created_at ?? 0;
  const agents: DeckMember[] = agentsRes.agents.map((a) => ({
    id: `${deckId}-agent-${a.slug}`,
    deck_id: deckId,
    member_id: a.tulpa_id,
    member_type: 'agent',
    role: 'agent',
    tulpa_id: a.slug,
    joined_at: agentJoinedAt,
    status: 'idle',
  }));
  return [...humans, ...agents];
}

export async function inviteDeckMember(
  deckId: string,
  input: { email?: string; user_id?: string; role?: 'owner' | 'editor' | 'viewer' },
): Promise<DeckMember> {
  const res = await cfRequest<{ member: CfDeckMember }>(
    'POST',
    `/decks/${deckId}/members`,
    input,
  );
  return adaptDeckMember(deckId, res.member);
}

export async function setDeckMemberRole(
  deckId: string,
  userId: string,
  role: 'owner' | 'editor' | 'viewer',
): Promise<DeckMember> {
  const res = await cfRequest<{ member: CfDeckMember }>(
    'PATCH',
    `/decks/${deckId}/members/${encodeURIComponent(userId)}`,
    { role },
  );
  return adaptDeckMember(deckId, res.member);
}

export async function removeDeckMember(
  deckId: string,
  userId: string,
): Promise<void> {
  await cfRequest('DELETE', `/decks/${deckId}/members/${encodeURIComponent(userId)}`);
}

export async function transferDeckOwner(
  deckId: string,
  newOwnerId: string,
): Promise<DeckMember[]> {
  const res = await cfRequest<{ members: CfDeckMember[] }>(
    'POST',
    `/decks/${deckId}/members/${encodeURIComponent(newOwnerId)}/transfer-owner`,
  );
  return res.members.map((m) => adaptDeckMember(deckId, m));
}

// ── Messages ─────────────────────────────────────────────────

interface CfMessage {
  id: string;
  turn_id: string;
  role: 'user' | 'agent' | 'system';
  content: string;
  created_at: number;
  attachments?: ChatAttachment[];
}

function adaptMessage(m: CfMessage, deckId: string): Message {
  return {
    id: m.id,
    conversation_id: deckId,
    role: m.role === 'agent' ? 'assistant' : m.role,
    content: m.content,
    // App.tsx's transformMessages reads from `contentBlocks` (Arx wire
    // shape). CF stores plain text — synthesise a single text block so
    // hydration on reload doesn't drop the message body.
    contentBlocks: [{ kind: 'text', text: m.content }],
    tulpa_id: m.role === 'agent' ? 'pp-writer' : undefined,
    created_at: new Date(m.created_at).toISOString(),
    // Off-spec extension consumed by blocksToMessages / textOrRefMessages
    // (any-typed cast there). Keeps the Message type stable.
    ...(m.attachments && m.attachments.length > 0
      ? { _imageAttachments: m.attachments }
      : {}),
  } as Message;
}

export async function getMessages(
  deckId: string,
  limit = 50,
  _offset = 0,
): Promise<Message[]> {
  // CF API uses a `before` cursor instead of offset. For Phase A
  // parity, ignore offset and return the most-recent `limit`.
  const res = await cfRequest<{ messages: CfMessage[]; has_more: boolean }>(
    'GET',
    `/decks/${deckId}/messages?limit=${limit}`,
  );
  return res.messages.map((m) => adaptMessage(m, deckId));
}

// ── Context (memory / reference files) ───────────────────────

export interface ContextItem {
  key: string;
  content: string;
  content_type?: string;
  enabled?: number;
  created_at?: string | number;
  updated_at?: string | number;
  _binary?: boolean;
  _binary_name?: string;
  _binary_size?: number;
}

export async function listContext(deckId: string): Promise<ContextItem[]> {
  return cfRequest<ContextItem[]>('GET', `/decks/${deckId}/context`);
}

export async function saveContext(
  deckId: string,
  key: string,
  content: string,
  contentType?: string,
): Promise<ContextItem> {
  return cfRequest<ContextItem>('POST', `/decks/${deckId}/context`, {
    key,
    content,
    ...(contentType ? { content_type: contentType } : {}),
  });
}

export async function deleteContext(
  deckId: string,
  key: string,
): Promise<void> {
  await cfRequest<{ ok: true }>(
    'DELETE',
    `/decks/${deckId}/context/${encodeURIComponent(key)}`,
  );
}

export async function toggleContext(
  deckId: string,
  key: string,
  enabled: boolean,
): Promise<{ key: string; enabled: boolean }> {
  return cfRequest<{ key: string; enabled: boolean }>(
    'POST',
    `/decks/${deckId}/context/toggle`,
    { key, enabled },
  );
}

export async function uploadContextFile(
  deckId: string,
  file: File,
  section: string = 'context',
  skillGroup?: string,
  onProgress?: (loaded: number, total: number) => void,
): Promise<{ key: string; r2_key: string; content_type: string; name: string }> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('section', section);
  if (skillGroup) formData.append('skillGroup', skillGroup);
  // Use XHR-backed apiUpload when a progress callback is provided so
  // the composer can stream byte-level progress to the UI; otherwise
  // any fetch path would do.
  const res = await apiUpload(`${API}/decks/${deckId}/context/upload`, formData, { onProgress });
  if (!res.ok) {
    let msg = `upload failed (${res.status})`;
    try {
      const body = (await res.json()) as { error?: { message?: string } };
      if (body.error?.message) msg = body.error.message;
    } catch {
      /* not json */
    }
    throw new Error(msg);
  }
  return (await res.json()) as {
    key: string;
    r2_key: string;
    content_type: string;
    name: string;
  };
}

export function contextDownloadUrl(deckId: string, key: string): string {
  return `${API}/decks/${deckId}/context/download?key=${encodeURIComponent(key)}`;
}

// ── Folders ──────────────────────────────────────────────────

interface CfFolder {
  id: string;
  workspace_id: string;
  parent_id: string | null;
  name: string;
  sort_order: number;
  deck_count?: number;
  created_at: number;
  updated_at: number;
  deleted_at: number | null;
}

function adaptFolder(f: CfFolder): Folder {
  return {
    id: f.id,
    workspace_id: f.workspace_id,
    parent_id: f.parent_id,
    name: f.name,
    sort_order: f.sort_order,
    deck_count: f.deck_count ?? 0,
    created_at: f.created_at,
    updated_at: f.updated_at,
    deleted_at: f.deleted_at,
  };
}

export async function getFolders(workspaceId: string): Promise<Folder[]> {
  const res = await cfRequest<{ folders: CfFolder[] }>(
    'GET',
    `/workspaces/${workspaceId}/folders`,
  );
  return (res.folders ?? []).map(adaptFolder);
}

export async function createFolder(
  workspaceId: string,
  name: string,
  parentId?: string,
): Promise<Folder> {
  const res = await cfRequest<{ folder: CfFolder }>(
    'POST',
    `/workspaces/${workspaceId}/folders`,
    { name, ...(parentId ? { parent_id: parentId } : {}) },
  );
  return adaptFolder(res.folder);
}

export async function updateFolder(
  folderId: string,
  data: { name?: string; parent_id?: string | null },
): Promise<Folder> {
  const res = await cfRequest<{ folder: CfFolder }>(
    'PATCH',
    `/folders/${folderId}`,
    data,
  );
  return adaptFolder(res.folder);
}

export async function deleteFolder(
  folderId: string,
  confirmName: string,
): Promise<{ folders_deleted: number; decks_moved: number }> {
  return cfRequest<{ folders_deleted: number; decks_moved: number }>(
    'DELETE',
    `/folders/${folderId}`,
    { confirm_name: confirmName },
  );
}

export async function moveDeckToFolder(
  deckId: string,
  folderId: string | null,
): Promise<Deck> {
  const res = await cfRequest<{ deck: CfDeck }>('PATCH', `/decks/${deckId}`, {
    folder_id: folderId,
  });
  return adaptDeck(res.deck);
}

// ── Sharing ──────────────────────────────────────────────────

export interface ShareInfo {
  id: string;
  deck_id: string;
  share_token: string;
  title: string;
  url: string;
  view_count: number;
  created_at: number;
  updated_at: number;
}

export async function createShare(deckId: string): Promise<ShareInfo> {
  return cfRequest<ShareInfo>('POST', `/decks/${deckId}/share`);
}

export async function getShare(deckId: string): Promise<ShareInfo | null> {
  return cfRequest<ShareInfo | null>('GET', `/decks/${deckId}/share`);
}

export async function revokeShare(deckId: string): Promise<void> {
  await cfRequest<{ ok: true }>('DELETE', `/decks/${deckId}/share`);
}

// ── Profiles ─────────────────────────────────────────────────

export const listProfiles = () => cfRequest<Profile[]>('GET', '/profiles');

export const getMyProfile = () =>
  cfRequest<Profile | null>('GET', '/profiles/me');

export const updateMyProfile = (updates: Partial<Profile>) =>
  cfRequest<Profile>('PATCH', '/profiles/me', updates);

export async function uploadAvatar(file: File): Promise<Profile> {
  const formData = new FormData();
  formData.append('avatar', file);
  const res = await apiFetch(`${API}/profiles/me/avatar`, {
    method: 'POST',
    body: formData,
  });
  if (!res.ok) {
    let msg = `upload failed (${res.status})`;
    try {
      const body = (await res.json()) as { error?: { message?: string } };
      if (body.error?.message) msg = body.error.message;
    } catch {
      /* not json */
    }
    throw new Error(msg);
  }
  return (await res.json()) as Profile;
}

// ── Artifacts / Storyboard ───────────────────────────────────

export interface ArtifactItem {
  id: string;
  filename: string;
  key: string;
  created_at?: string | number;
  updated_at?: string | number;
}

export async function getArtifacts(deckId: string): Promise<ArtifactItem[]> {
  const res = await cfRequest<{
    ok: boolean;
    data?: ArtifactItem[];
  }>('GET', `/decks/${deckId}/artifacts?_t=${Date.now()}`);
  return Array.isArray(res.data) ? res.data : [];
}

export interface StoryboardData {
  cards: {
    id: string;
    slideNum: number;
    title: string;
    body: string;
    tag: string;
  }[];
  audience?: string;
  narrative?: string;
}

/** Parse `## Slide N: Title\n- bullet\n- bullet` blocks out of an
 * agent message into a storyboard. Until the backend has a server-side
 * storyboard endpoint we extract from the latest agent reply on the
 * client side. */
function parseStoryboard(content: string): StoryboardData['cards'] {
  const cards: StoryboardData['cards'] = [];
  const re = /^##\s*Slide\s*(\d+)\s*:\s*(.+)$/gim;
  const matches: { idx: number; n: number; title: string }[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(content)) !== null) {
    matches.push({ idx: m.index, n: parseInt(m[1], 10), title: m[2].trim() });
  }
  for (let i = 0; i < matches.length; i++) {
    const start = matches[i].idx;
    const end = i + 1 < matches.length ? matches[i + 1].idx : content.length;
    const block = content.slice(start, end);
    const body = block.replace(/^##\s*Slide\s*\d+\s*:\s*.+$/im, '').trim();
    cards.push({
      id: `slide-${matches[i].n}`,
      slideNum: matches[i].n,
      title: matches[i].title,
      body,
      tag: '',
    });
  }
  return cards;
}

export async function getStoryboard(
  deckId: string,
): Promise<StoryboardData | null> {
  // Pull the most recent agent message and parse slide blocks out
  // of it as a fallback storyboard.
  try {
    const messages = await getMessages(deckId, 20);
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.role !== 'assistant') continue;
      const cards = parseStoryboard(m.content);
      if (cards.length > 0) return { cards };
    }
    return { cards: [] };
  } catch {
    return null;
  }
}

// ── Confluence (parity slices 2–4) ─────────────────────────────

export interface ConfluenceConnection {
  id: string;
  user_id: string;
  cloud_id: string;
  cloud_url: string;
  display_name: string | null;
  scopes: string | null;
  created_at: number;
  updated_at: number;
}

export interface ConfluenceSpace {
  id: string;
  key: string;
  name: string;
  type: string;
}

export interface ConfluencePage {
  id: string;
  title: string;
  type: string;
  spaceKey: string;
  parentId: string | null;
  version: number;
  url: string;
  referenced: boolean;
  memoryKey: string | null;
}

interface OkData<T> {
  ok: boolean;
  data: T;
}

export function confluenceAuthUrl(): string {
  return `${API}/auth/confluence`;
}

export async function listConfluenceConnections(): Promise<ConfluenceConnection[]> {
  try {
    const r = await cfRequest<OkData<ConfluenceConnection[]>>(
      'GET',
      '/confluence/connections',
    );
    return r.data ?? [];
  } catch {
    return [];
  }
}

export async function deleteConfluenceConnection(id: string): Promise<void> {
  await cfRequest<unknown>(
    'DELETE',
    `/confluence/connections/${encodeURIComponent(id)}`,
  );
}

export async function listConfluenceSpaces(
  deckId: string,
  connectionId?: string,
): Promise<ConfluenceSpace[]> {
  const qs = connectionId
    ? `?connection_id=${encodeURIComponent(connectionId)}`
    : '';
  try {
    const r = await cfRequest<OkData<ConfluenceSpace[]>>(
      'GET',
      `/decks/${encodeURIComponent(deckId)}/confluence/spaces${qs}`,
    );
    return r.data ?? [];
  } catch {
    return [];
  }
}

export async function listConfluencePages(
  deckId: string,
  spaceKey: string,
  connectionId?: string,
  parentId?: string,
): Promise<ConfluencePage[]> {
  const params = new URLSearchParams({ space_key: spaceKey });
  if (connectionId) params.set('connection_id', connectionId);
  if (parentId) params.set('parent_id', parentId);
  try {
    const r = await cfRequest<OkData<ConfluencePage[]>>(
      'GET',
      `/decks/${encodeURIComponent(deckId)}/confluence/pages?${params}`,
    );
    return r.data ?? [];
  } catch {
    return [];
  }
}

// Lightweight "what does this page look like" preview. The server
// doesn't expose a dedicated /preview endpoint yet — agent and user
// both use the reference flow's response. So we cheat by calling
// search with the title as a probe; if the agent really needs the
// body it should reference it. Frontend uses this for the modal
// preview card.
export async function previewConfluencePage(
  deckId: string,
  pageId: string,
  _connectionId?: string,
): Promise<{
  title: string;
  markdown: string;
  version: number;
  pageUrl: string;
}> {
  // Fall back to listConfluencePages and look the page up; the
  // server's pages route doesn't include body. For now return a
  // stub-but-shaped response so the UI keeps working.
  const pages = await listConfluencePages(deckId, '', _connectionId);
  const found = pages.find((p) => p.id === pageId);
  return {
    title: found?.title ?? '',
    markdown: '',
    version: found?.version ?? 0,
    pageUrl: found?.url ?? '',
  };
}

export async function referenceConfluencePage(
  deckId: string,
  pageId: string,
  connectionId?: string,
  _type?: string,
): Promise<{
  key: string;
  title: string;
  status: 'created' | 'updated' | 'unchanged';
}> {
  const r = await cfRequest<
    OkData<{
      page_id: string;
      title: string;
      memory_key: string;
      version: number | null;
      url: string;
    }>
  >('POST', `/decks/${encodeURIComponent(deckId)}/confluence/reference`, {
    page_id: pageId,
    connection_id: connectionId ?? null,
  });
  return {
    key: r.data.memory_key,
    title: r.data.title,
    // Server doesn't distinguish created/updated/unchanged; the UI
    // only uses the "title was attached" event so 'created' is fine.
    status: 'created',
  };
}

export async function removeConfluenceReference(
  deckId: string,
  pageId: string,
): Promise<void> {
  await cfRequest<unknown>(
    'DELETE',
    `/decks/${encodeURIComponent(deckId)}/confluence/reference/${encodeURIComponent(pageId)}`,
  );
}

// ── Droploft (parity slice 5) ───────────────────────────────────

export interface DroploftConnection {
  id: string;
  user_id: string;
  base_url: string;
  display_name: string | null;
  email: string | null;
  created_at: number;
  updated_at: number;
}

export interface DroploftDocument {
  id: string;
  slug: string;
  title: string;
  url: string;
  excerpt: string | null;
  version_number: number | null;
  updated_at: number | null;
  content_type: 'html' | 'markdown' | null;
}

export async function listDroploftConnections(): Promise<DroploftConnection[]> {
  try {
    const r = await cfRequest<OkData<DroploftConnection[]>>(
      'GET',
      '/droploft/connections',
    );
    return r.data ?? [];
  } catch {
    return [];
  }
}

export async function addDroploftConnection(input: {
  apiKey: string;
  baseUrl?: string;
  displayName?: string;
}): Promise<DroploftConnection> {
  const r = await cfRequest<OkData<DroploftConnection>>(
    'POST',
    '/droploft/connections',
    {
      api_key: input.apiKey,
      base_url: input.baseUrl ?? null,
      display_name: input.displayName ?? null,
    },
  );
  return r.data;
}

export async function deleteDroploftConnection(id: string): Promise<void> {
  await cfRequest<unknown>(
    'DELETE',
    `/droploft/connections/${encodeURIComponent(id)}`,
  );
}

export async function searchDroploftDocuments(
  deckId: string,
  query?: string,
  connectionId?: string,
  limit = 20,
): Promise<DroploftDocument[]> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (query?.trim()) params.set('q', query.trim());
  if (connectionId) params.set('connection_id', connectionId);
  try {
    const r = await cfRequest<OkData<DroploftDocument[]>>(
      'GET',
      `/decks/${encodeURIComponent(deckId)}/droploft/search?${params}`,
    );
    return r.data ?? [];
  } catch {
    return [];
  }
}

export async function getDroploftDocument(
  deckId: string,
  docId: string,
  connectionId?: string,
): Promise<DroploftDocument & { body: string | null }> {
  const qs = connectionId
    ? `?connection_id=${encodeURIComponent(connectionId)}`
    : '';
  const r = await cfRequest<OkData<DroploftDocument & { body: string | null }>>(
    'GET',
    `/decks/${encodeURIComponent(deckId)}/droploft/document/${encodeURIComponent(docId)}${qs}`,
  );
  return r.data;
}

export async function importDroploftDocument(
  deckId: string,
  documentId: string,
  connectionId?: string,
): Promise<{
  document_id: string;
  title: string | null;
  memory_key: string | null;
  url: string | null;
}> {
  const r = await cfRequest<
    OkData<{
      document_id: string;
      title: string | null;
      memory_key: string | null;
      url: string | null;
    }>
  >('POST', `/decks/${encodeURIComponent(deckId)}/droploft/reference`, {
    document_id: documentId,
    connection_id: connectionId ?? null,
  });
  return r.data;
}

export async function removeDroploftReference(
  deckId: string,
  docId: string,
): Promise<void> {
  await cfRequest<unknown>(
    'DELETE',
    `/decks/${encodeURIComponent(deckId)}/droploft/reference/${encodeURIComponent(docId)}`,
  );
}

export async function publishDroploftDeck(
  deckId: string,
  input: { name?: string; changelog?: string; connectionId?: string },
): Promise<{
  id: string;
  slug: string;
  url: string;
  version_number: number | null;
}> {
  const r = await cfRequest<
    OkData<{
      id: string;
      slug: string;
      url: string;
      version_number: number | null;
    }>
  >('POST', `/decks/${encodeURIComponent(deckId)}/droploft/publish`, {
    name: input.name ?? null,
    changelog: input.changelog ?? null,
    connection_id: input.connectionId ?? null,
  });
  return r.data;
}

// ── Admin app_settings (Connections + Configuration tabs) ─────
//
// Backed by /api/admin/settings (apps/api/src/db/appSettingsRepo.ts).
// GET returns sensitive values masked ("abcd••••xy") so a page
// refresh doesn't leak credentials. PUT accepts cleartext and
// returns the refreshed (still-masked) map.

export interface SettingValue {
  value: string;
  source: 'default' | 'database' | 'db' | 'env';
  updated_at?: number;
}
export type AdminSettings = Record<string, SettingValue>;

export async function getAdminSettings(): Promise<AdminSettings> {
  return cfRequest<AdminSettings>('GET', '/admin/settings');
}

export async function updateAdminSettings(
  patch: Record<string, string>,
): Promise<AdminSettings> {
  return cfRequest<AdminSettings>('PUT', '/admin/settings', patch);
}

export async function testArxConnection(
  _url?: string,
): Promise<{ status: number; body: unknown }> {
  // PP-CF doesn't talk to arx — the test is N/A. Kept as a stub so
  // the existing ConnectionsManager UI code doesn't have to be
  // conditionalised; UI hides the button instead.
  throw new NotImplementedError('Arx connection test');
}

export async function publishTemplate(
  _deckId: string,
  _templateId: string,
  _title: string,
): Promise<{ templateId: string; files: string[] }> {
  throw new NotImplementedError('Template publishing');
}
